import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useStatuses } from "@/context/StatusContext";
import { buildWhatsappUrl } from "@/lib/whatsapp";
import { DateRangePicker, toISODate } from "@/components/DateRangePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Package, AlertTriangle, Clock, CheckCircle2, Loader2, Search, MessageCircle, ExternalLink, ArrowRight, Undo2, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

function Kpi({ icon: Icon, label, value, accent, testid, sub, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="text-left rounded-lg border border-border bg-white p-5 fade-up transition-all duration-200 hover:shadow-md hover:border-[#0B3D3D]/40 cursor-pointer">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="font-heading text-3xl font-extrabold text-[#111411] mt-3 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [couriers, setCouriers] = useState([]);
  const [range, setRange] = useState(undefined);
  const { metaMap, stageMap } = useStatuses();
  const navigate = useNavigate();

  const dateParams = useMemo(() => {
    const p = {};
    if (range?.from) p.from_date = toISODate(range.from);
    if (range?.to) p.to_date = toISODate(range.to);
    return p;
  }, [range]);

  useEffect(() => {
    api.get("/analytics/summary", { params: dateParams }).then((r) => setData(r.data));
  }, [dateParams]);

  useEffect(() => {
    api.get("/shipments/couriers").then((r) => setCouriers(r.data)).catch(() => {});
  }, []);

  const actionData = data ? Object.entries(data.status_counts).map(([k, v]) => ({ name: metaMap[k]?.label || k, value: v })) : [];
  const stageData = data ? Object.entries(data.stage_counts || {}).map(([k, v]) => ({ name: stageMap[k]?.label || k, value: v })) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-[#111411] tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">NDR pipeline insights · resolution window {data?.resolution_days ?? "…"} days from dispatch.</p>
        </div>
        <div>
          <DateRangePicker range={range} onChange={setRange} testid="analytics-date" />
          <p className="text-xs text-muted-foreground mt-1.5 sm:text-right">Filters all tabs by dispatch date</p>
        </div>
      </div>

      {!data ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="analytics-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading analytics…</div>
      ) : (
      <Tabs defaultValue="overview">
        <TabsList data-testid="analytics-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="explore" data-testid="tab-explore">Explore &amp; Follow-up</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Courier Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi icon={Package} label="Total Shipments" value={data.total} accent="bg-[#E8ECE8] text-[#0B3D3D]" testid="kpi-total" sub={data.payment ? `Prepaid ${data.payment.total.prepaid} · COD ${data.payment.total.cod}` : undefined} onClick={() => navigate("/shipments?view=all")} />
            <Kpi icon={Clock} label="Open / In-progress" value={data.open} accent="bg-[#FBEEE6] text-[#B45309]" testid="kpi-open" sub={data.payment ? `Prepaid ${data.payment.open.prepaid} · COD ${data.payment.open.cod}` : undefined} onClick={() => navigate("/shipments?view=open")} />
            <Kpi icon={AlertTriangle} label="Overdue" value={data.overdue} accent="bg-[#F7E4DF] text-[#C24127]" testid="kpi-overdue" sub="Past resolution deadline" onClick={() => navigate("/shipments?view=overdue")} />
            <Kpi icon={CheckCircle2} label="Delivered" value={data.delivered} accent="bg-[#E3F0E5] text-[#1E7A34]" testid="kpi-delivered" sub={data.payment ? `Prepaid ${data.payment.delivered.prepaid} · COD ${data.payment.delivered.cod}` : undefined} onClick={() => navigate("/shipments?view=delivered")} />
          </div>

          {data.by_direction && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="direction-breakdown">
              {[
                { key: "forward", label: "Forward", Icon: ArrowRight, accent: "text-[#1D4E89]", bg: "bg-[#E4ECF7]" },
                { key: "return", label: "Return (RTO)", Icon: Undo2, accent: "text-[#C24127]", bg: "bg-[#FBE4DC]" },
              ].map(({ key, label, Icon, accent, bg }) => {
                const d = data.by_direction[key] || {};
                const _t = d.total || 0;
                const pct = (v) => (_t ? Math.round(((v || 0) / _t) * 100) : 0);
                return (
                  <div key={key} className="rounded-lg border border-border bg-white p-5" data-testid={`dir-card-${key}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className={`h-9 w-9 rounded-lg ${bg} ${accent} flex items-center justify-center`}><Icon className="h-4 w-4" /></span>
                      <h3 className="font-heading font-bold text-[#111411]">{label}</h3>
                      <span className="ml-auto text-2xl font-bold text-[#111411]" data-testid={`dir-total-${key}`}>{d.total ?? 0}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div><p className="text-lg font-bold text-[#B45309]">{d.open ?? 0} <span className="text-xs font-medium text-muted-foreground">({pct(d.open)}%)</span></p><p className="text-xs text-muted-foreground">Open</p></div>
                      <div><p className="text-lg font-bold text-[#C24127]">{d.overdue ?? 0} <span className="text-xs font-medium text-muted-foreground">({pct(d.overdue)}%)</span></p><p className="text-xs text-muted-foreground">Overdue</p></div>
                      <div><p className="text-lg font-bold text-[#1E7A34]">{d.resolved ?? 0} <span className="text-xs font-medium text-muted-foreground">({pct(d.resolved)}%)</span></p><p className="text-xs text-muted-foreground">Resolved</p></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      Avg resolution: {d.avg_resolution_days != null ? `${d.avg_resolution_days} days` : "—"}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-resolution">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div>
                <h3 className="font-heading font-bold text-[#111411]">Open vs Resolved</h3>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {data.total ? Math.round((data.resolved / data.total) * 100) : 0}% resolved of {data.total} total shipments
                </p>
                {data.avg_resolution_days != null && (
                  <p className="text-sm mt-1" data-testid="avg-resolution-days">
                    <span className="font-semibold text-[#0B3D3D]">{data.avg_resolution_days}</span>
                    <span className="text-muted-foreground"> avg days to resolve</span>
                  </p>
                )}
              </div>
              <div className="flex items-center gap-8">
                <button onClick={() => navigate("/shipments?view=open")} className="text-left transition-transform hover:scale-105" data-testid="resolution-open">
                  <p className="text-2xl font-extrabold text-[#B45309]">{data.open}</p>
                  <p className="text-xs text-muted-foreground">Open</p>
                </button>
                <button onClick={() => navigate("/shipments?view=resolved")} className="text-left transition-transform hover:scale-105" data-testid="resolution-resolved">
                  <p className="text-2xl font-extrabold text-[#1E7A34]">{data.resolved}</p>
                  <p className="text-xs text-muted-foreground">Resolved</p>
                </button>
              </div>
              <div className="w-full sm:w-72">
                <div className="h-3 rounded-full overflow-hidden bg-[#EEF0EE] flex">
                  <div className="h-full bg-[#D48B28] transition-all duration-500" style={{ width: `${data.total ? (data.open / data.total) * 100 : 0}%` }} />
                  <div className="h-full bg-[#1FA855] transition-all duration-500" style={{ width: `${data.total ? (data.resolved / data.total) * 100 : 0}%` }} />
                </div>
                <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5">
                  <span>Open</span><span>Resolved</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-payment-donut">
              <h3 className="font-heading font-bold text-[#111411] mb-1">Payment mix</h3>
              <p className="text-sm text-muted-foreground mb-2">Prepaid vs COD across all shipments</p>
              {(() => {
                const pre = data.payment?.total?.prepaid || 0;
                const cod = data.payment?.total?.cod || 0;
                const tot = pre + cod;
                const pieData = [
                  { name: "Prepaid", value: pre, color: "#1FA855" },
                  { name: "COD", value: cod, color: "#D48B28" },
                ];
                if (!tot) return <div className="h-[200px] flex items-center justify-center text-sm text-muted-foreground">No payment data</div>;
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none">
                        {pieData.map((e) => <Cell key={e.name} fill={e.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} (${Math.round((v / tot) * 100)}%)`, n]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
            <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-status">
              <h3 className="font-heading font-bold text-[#111411] mb-4">By status</h3>
              <div className="space-y-3">
                {stageData.map((s) => (
                  <div key={s.name}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">{s.name}</span>
                      <span className="font-semibold text-[#111411]">{s.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#EEF0EE] overflow-hidden">
                      <div className="h-full bg-[#0B3D3D] transition-all duration-500" style={{ width: `${data.total ? (s.value / data.total) * 100 : 0}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-trends">
            <h3 className="font-heading font-bold text-[#111411] mb-4">Trend · last 14 days</h3>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.trends} margin={{ left: -18, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#5C635C" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#5C635C" }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip /><Legend />
                <Line type="monotone" dataKey="added" name="Added" stroke="#0B3D3D" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#1FA855" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="delivered" name="Delivered" stroke="#D48B28" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-final-action">
            <h3 className="font-heading font-bold text-[#111411] mb-1">By final action required</h3>
            <p className="text-xs text-muted-foreground mb-4">What each shipment needs the NDR team to do next</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {actionData.map((s) => (
                <div key={s.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">{s.name}</span>
                    <span className="font-semibold text-[#111411]">{s.value}</span>
                  </div>
                  <div className="h-2 rounded-full bg-[#EEF0EE] overflow-hidden">
                    <div className="h-full bg-[#C5A253] transition-all duration-500" style={{ width: `${data.total ? (s.value / data.total) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-courier">
              <h3 className="font-heading font-bold text-[#111411] mb-4">Volume per courier</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.per_courier} margin={{ left: -18, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                  <XAxis dataKey="courier" tick={{ fontSize: 11, fill: "#5C635C" }} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 12, fill: "#5C635C" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="total" name="Total" fill="#0B3D3D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="delivered" name="Delivered" fill="#8FB996" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="overdue" name="Overdue" fill="#C24127" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-agent">
              <h3 className="font-heading font-bold text-[#111411] mb-4">Performance per agent</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.per_agent} margin={{ left: -18, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                  <XAxis dataKey="agent" tick={{ fontSize: 11, fill: "#5C635C" }} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 12, fill: "#5C635C" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="total" name="Assigned" fill="#0B3D3D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="delivered" name="Delivered" fill="#8FB996" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="open" name="Open" fill="#D48B28" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {Array.isArray(data.overdue_by_zone) && data.overdue_by_zone.length > 0 && (
            <div className="rounded-lg border border-border bg-white p-5" data-testid="chart-zone">
              <h3 className="font-heading font-bold text-[#111411] mb-1">Overdue by delivery zone</h3>
              <p className="text-sm text-muted-foreground mb-4">Which courier TAT zones slip the most — spot problem lanes at a glance.</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.overdue_by_zone} margin={{ left: -18, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
                  <XAxis dataKey="zone" tick={{ fontSize: 11, fill: "#5C635C" }} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12, fill: "#5C635C" }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip /><Legend />
                  <Bar dataKey="total" name="Total" fill="#0B3D3D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="overdue" name="Overdue" fill="#C24127" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        <TabsContent value="explore" className="mt-6">
          <ExploreTab couriers={couriers} dateParams={dateParams} />
        </TabsContent>

        <TabsContent value="performance" className="mt-6">
          <PerformanceTab dateParams={dateParams} />
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}

function ExploreTab({ couriers, dateParams }) {
  const { statuses } = useStatuses();
  const [courier, setCourier] = useState("all");
  const [minDays, setMinDays] = useState("");
  const [resolved, setResolved] = useState("unresolved");
  const [status, setStatus] = useState("all");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const apply = async () => {
    setLoading(true);
    setSelected({});
    const params = { courier, resolved, status, ...dateParams };
    if (minDays !== "") params.min_days_since_dispatch = Number(minDays);
    try {
      const { data } = await api.get("/analytics/query", { params });
      setResult(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { apply(); /* eslint-disable-next-line */ }, [dateParams]);

  const rows = result?.shipments || [];
  const selectedRows = useMemo(() => rows.filter((r) => selected[r.id]), [rows, selected]);
  const allChecked = rows.length > 0 && selectedRows.length === rows.length;

  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(rows.map((r) => [r.id, true])));
  };
  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const whatsapp = () => {
    const target = selectedRows.length ? selectedRows : rows;
    if (!target.length) return;
    window.open(buildWhatsappUrl(target), "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-white p-5">
        <h3 className="font-heading font-bold text-[#111411] mb-4">Build a view</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label>Courier partner</Label>
            <Select value={courier} onValueChange={setCourier}>
              <SelectTrigger className="mt-1.5" data-testid="explore-courier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All couriers</SelectItem>
                {couriers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Open more than (days since dispatch)</Label>
            <Input type="number" min={0} placeholder="e.g. 7" value={minDays}
                   onChange={(e) => setMinDays(e.target.value)} className="mt-1.5" data-testid="explore-min-days" />
          </div>
          <div>
            <Label>Resolution</Label>
            <Select value={resolved} onValueChange={setResolved}>
              <SelectTrigger className="mt-1.5" data-testid="explore-resolved"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unresolved">Unresolved only</SelectItem>
                <SelectItem value="resolved">Resolved only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="explore-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button onClick={apply} disabled={loading} data-testid="explore-apply" className="gap-2 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
            <Search className="h-4 w-4" /> Apply
          </Button>
          {result && <span className="text-sm text-muted-foreground" data-testid="explore-count">{result.count} shipment(s) match</span>}
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selectedRows.length ? `${selectedRows.length} selected` : "Select shipments to follow up, or WhatsApp all matches"}
          </p>
          <Button onClick={whatsapp} data-testid="explore-whatsapp" className="gap-2 bg-[#1FA855] hover:bg-[#178a45] text-white">
            <MessageCircle className="h-4 w-4" /> WhatsApp {selectedRows.length ? `(${selectedRows.length})` : "all"}
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F6F4]">
              <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="explore-select-all" /></TableHead>
              <TableHead>AWB</TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Courier</TableHead>
              <TableHead>Dispatch date</TableHead>
              <TableHead>Last update</TableHead>
              <TableHead>Days open</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Track</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No shipments match these filters.</TableCell></TableRow>
            ) : rows.map((s) => (
              <TableRow key={s.id} data-testid={`explore-row-${s.id}`} className={s.is_overdue ? "border-l-4 border-l-[#C24127]" : ""}>
                <TableCell><Checkbox checked={!!selected[s.id]} onCheckedChange={() => toggle(s.id)} data-testid={`explore-check-${s.id}`} /></TableCell>
                <TableCell className="font-medium">{s.tracking_number}</TableCell>
                <TableCell>{s.order_id}</TableCell>
                <TableCell className="text-sm">{s.courier_partner}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.pickup_date)}</TableCell>
                <TableCell className="text-sm">{fmtDateTime(s.updated_at)}</TableCell>
                <TableCell className={`text-sm font-medium ${s.is_overdue ? "text-[#C24127]" : ""}`}>{s.days_since_dispatch ?? "—"}</TableCell>
                <TableCell><StatusBadge status={s.status} /></TableCell>
                <TableCell>
                  {s.tracking_link && (
                    <a href={s.tracking_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#0B3D3D] hover:underline">
                      Track <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PerformanceTab({ dateParams }) {
  const [level, setLevel] = useState("city");
  const [payment, setPayment] = useState("all");
  const [geo, setGeo] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [courierFilter, setCourierFilter] = useState("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [resSort, setResSort] = useState({ key: "total", dir: "desc" });
  const [geoSort, setGeoSort] = useState({ key: "total", dir: "desc" });
  const sortRows = (rows, sort) => {
    const { key, dir } = sort;
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      const an = av == null, bn = bv == null;
      if (an && bn) return 0;
      if (an) return 1;
      if (bn) return -1;
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (dir === "asc" ? 1 : -1);
    });
  };
  const toggleSort = (setSort, key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  const SortHead = ({ label, sub, col, sort, onSort, testid }) => (
    <TableHead>
      <button type="button" onClick={() => onSort(col)} data-testid={testid}
              className="inline-flex items-center gap-1 hover:text-[#0B3D3D] transition-colors">
        <span>{label}</span>
        {sort.key === col
          ? (sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
          : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
      </button>
      {sub && <div className="text-[10px] font-normal text-muted-foreground normal-case">{sub}</div>}
    </TableHead>
  );

  const load = async () => {
    setLoading(true);
    const params = { ...dateParams };
    if (payment !== "all") params.payment = payment;
    try {
      const [g, r] = await Promise.all([
        api.get("/analytics/geo", { params: { level, ...params } }),
        api.get("/analytics/resolution", { params }),
      ]);
      setGeo(g.data); setRes(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [level, payment, dateParams]);
  useEffect(() => { setPage(1); }, [level, citySearch, courierFilter]);

  const allRows = geo?.rows || [];
  const courierOptions = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.courier).filter(Boolean))).sort(),
    [geo]); // eslint-disable-line
  const filtered = allRows.filter((r) =>
    (!citySearch || String(r.region).toLowerCase().includes(citySearch.toLowerCase())) &&
    (courierFilter === "all" || r.courier === courierFilter));
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pageCount);
  const paged = sortRows(filtered, geoSort).slice((cur - 1) * pageSize, cur * pageSize);

  const resRows = res?.rows || [];
  const totalsRow = res?.totals;
  const sortedRes = sortRows(resRows, resSort);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-white p-5 flex flex-wrap items-end gap-4">
        <div>
          <Label>Group performance by</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="mt-1.5 w-40" data-testid="perf-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pincode">PIN code</SelectItem>
              <SelectItem value="city">City</SelectItem>
              <SelectItem value="state">State</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Payment</Label>
          <Select value={payment} onValueChange={setPayment}>
            <SelectTrigger className="mt-1.5 w-40" data-testid="perf-payment"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="prepaid">Prepaid</SelectItem>
              <SelectItem value="cod">COD</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mb-2" />}
      </div>

      <div className="rounded-lg border border-border bg-white p-5" data-testid="perf-resolution">
        <h3 className="font-heading font-bold text-[#111411] mb-1">Courier performance summary</h3>
        <p className="text-sm text-muted-foreground mb-4">Per courier: total shipments, how many are resolved, average days to resolve (from added to the panel) and average days to deliver (from pickup). Top row aggregates all couriers.</p>
        {resRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No shipments in this window yet.</p>
        ) : (
          <Table>
            <TableHeader><TableRow className="bg-[#F4F6F4]">
              <TableHead>Courier</TableHead>
              <SortHead label="Total" col="total" sort={resSort} onSort={(k) => toggleSort(setResSort, k)} testid="sort-res-total" />
              <SortHead label="Resolved" col="resolved" sort={resSort} onSort={(k) => toggleSort(setResSort, k)} testid="sort-res-resolved" />
              <SortHead label="Avg resolution (days)" sub="from added in panel" col="avg_resolution_days" sort={resSort} onSort={(k) => toggleSort(setResSort, k)} testid="sort-res-avgres" />
              <TableHead><div>Avg delivery (days)</div><div className="text-[10px] font-normal text-muted-foreground normal-case">from pickup date</div></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {totalsRow && totalsRow.total > 0 && (
                <TableRow className="bg-[#F0F4F1] font-semibold" data-testid="perf-res-totals">
                  <TableCell className="text-[#0B3D3D]">All couriers</TableCell>
                  <TableCell>{totalsRow.total}</TableCell>
                  <TableCell>{totalsRow.resolved}</TableCell>
                  <TableCell className="text-[#0B3D3D]">{totalsRow.avg_resolution_days ?? "—"}</TableCell>
                  <TableCell className="text-[#0B3D3D]">{totalsRow.avg_delivery_days ?? "—"}</TableCell>
                </TableRow>
              )}
              {sortedRes.map((r) => (
                <TableRow key={r.courier} data-testid={`perf-res-row-${r.courier}`}>
                  <TableCell className="font-medium">{r.courier}</TableCell>
                  <TableCell>{r.total}</TableCell>
                  <TableCell>{r.resolved}</TableCell>
                  <TableCell className="font-semibold text-[#0B3D3D]">{r.avg_resolution_days ?? "—"}</TableCell>
                  <TableCell>{r.avg_delivery_days ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-lg border border-border bg-white p-5" data-testid="perf-geo">
        <h3 className="font-heading font-bold text-[#111411] mb-1">Courier performance by {level}</h3>
        <p className="text-sm text-muted-foreground mb-4">Compare couriers per region to route future dispatches to the best performer.</p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search {level}</Label>
            <Input data-testid="perf-geo-search" value={citySearch} onChange={(e) => setCitySearch(e.target.value)}
                   placeholder={`Search ${level}…`} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Courier</Label>
            <Select value={courierFilter} onValueChange={setCourierFilter}>
              <SelectTrigger className="mt-1 w-48" data-testid="perf-geo-courier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All couriers</SelectItem>
                {courierOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data for this filter.</p>
        ) : (
          <>
            <Table>
              <TableHeader><TableRow className="bg-[#F4F6F4]">
                <TableHead className="capitalize">{level}</TableHead><TableHead>Courier</TableHead>
                <SortHead label="Total" col="total" sort={geoSort} onSort={(k) => toggleSort(setGeoSort, k)} testid="sort-geo-total" />
                <SortHead label="Delivered" col="delivered" sort={geoSort} onSort={(k) => toggleSort(setGeoSort, k)} testid="sort-geo-delivered" />
                <SortHead label="Delivery rate" col="delivery_rate" sort={geoSort} onSort={(k) => toggleSort(setGeoSort, k)} testid="sort-geo-rate" />
                <SortHead label="Overdue" col="overdue" sort={geoSort} onSort={(k) => toggleSort(setGeoSort, k)} testid="sort-geo-overdue" />
                <SortHead label="Avg resolution (days)" sub="from added in dashboard" col="avg_resolution_days" sort={geoSort} onSort={(k) => toggleSort(setGeoSort, k)} testid="sort-geo-avgres" />
              </TableRow></TableHeader>
              <TableBody>
                {paged.map((r) => (
                  <TableRow key={`${r.region}|${r.courier}`}>
                    <TableCell className="font-medium">{r.region}</TableCell>
                    <TableCell>{r.courier}</TableCell>
                    <TableCell>{r.total}</TableCell>
                    <TableCell>{r.delivered}</TableCell>
                    <TableCell>
                      <span className={r.delivery_rate >= 70 ? "text-[#1E7A34] font-semibold" : r.delivery_rate < 40 ? "text-[#C24127] font-semibold" : ""}>
                        {r.delivery_rate}%
                      </span>
                    </TableCell>
                    <TableCell className={r.overdue ? "text-[#C24127]" : ""}>{r.overdue}</TableCell>
                    <TableCell>{r.avg_resolution_days ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between mt-4 text-sm">
              <span className="text-muted-foreground" data-testid="perf-geo-range">
                {(cur - 1) * pageSize + 1}–{Math.min(cur * pageSize, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" data-testid="perf-geo-prev" disabled={cur <= 1} onClick={() => setPage(cur - 1)}>Prev</Button>
                <span className="text-muted-foreground">{cur} / {pageCount}</span>
                <Button variant="outline" size="sm" data-testid="perf-geo-next" disabled={cur >= pageCount} onClick={() => setPage(cur + 1)}>Next</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
